import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Archive, FileStack, Loader2, Radar } from "lucide-react";
import { toast } from "sonner";
import { ClientSatAutoSyncToggle, ClientFechaInicioDescargaField } from "@/components/ClientForm";
import SurgicalQueueStatusBanner from "@/components/SurgicalQueueStatusBanner";
import { useSatUiDiagnose } from "@/lib/useSatUiDiagnose";
import {
  CLIENT_XML_SISTEMA_LABELS,
  formatClientSatTimestamp,
  formatPeriodoCubiertoLabel,
  formatSchedulerClienteLabel,
  formatSyncAutoLabel,
  sumCfdiSummaryByDireccion,
} from "@/lib/clientSatStorageLabels";
import { useCanShowClientSatAutoSyncConfig } from "@/lib/clientSatAutoSyncVisibility";
import { syncEstadoColaLabel } from "@/lib/clientSatStorageLabels";

const SUMMARY_YEARS_BACK = 4;

/**
 * Panel de sincronización SAT por cliente (pestaña Clientes).
 * Solo configuración, estado informativo y navegación segura — sin descarga SAT directa.
 */
export default function ClientSatSyncPanel({ clientId, clients, onSelectClientId, onClientUpdated }) {
  const { enabled: diagnoseEnabled } = useSatUiDiagnose();
  const canShowSatAutoSync = useCanShowClientSatAutoSyncConfig();
  const [form, setForm] = useState({
    modulo_descarga_activo: false,
    fecha_inicio_descarga: "",
    estado_sincronizacion: "",
    sat_sync_completed_at: "",
    sat_sync_enqueued_at: "",
    sat_downloads_paused: false,
    sat_downloads_paused_reason: "",
    sat_5002_next_retry_at: "",
    sat_cooling_off_until: "",
  });
  const [savingFecha, setSavingFecha] = useState(false);
  const [surgicalQueue, setSurgicalQueue] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageSnapshot, setStorageSnapshot] = useState(null);

  const client = clients.find((c) => c.id === clientId) || null;

  useEffect(() => {
    if (!client) return;
    setForm({
      modulo_descarga_activo: Boolean(client.modulo_descarga_activo),
      fecha_inicio_descarga: (client.fecha_inicio_descarga || "").slice(0, 10),
      estado_sincronizacion: client.estado_sincronizacion || "",
      sat_sync_completed_at: client.sat_sync_completed_at || "",
      sat_sync_enqueued_at: client.sat_sync_enqueued_at || "",
      sat_downloads_paused: Boolean(client.sat_downloads_paused),
      sat_downloads_paused_reason: client.sat_downloads_paused_reason || "",
      sat_5002_next_retry_at: client.sat_5002_next_retry_at || "",
      sat_cooling_off_until: client.sat_cooling_off_until || "",
    });
  }, [client]);

  const loadSurgicalQueue = useCallback(async () => {
    if (!clientId || !diagnoseEnabled) {
      setSurgicalQueue(null);
      return;
    }
    try {
      const { data } = await api.get("/sat/admin/storage-summary", {
        params: { client_id: clientId },
      });
      setSurgicalQueue(data?.surgical_queue || null);
    } catch {
      setSurgicalQueue(null);
    }
  }, [clientId, diagnoseEnabled]);

  const loadStorageSnapshot = useCallback(async () => {
    if (!clientId) {
      setStorageSnapshot(null);
      return;
    }
    setStorageLoading(true);
    try {
      const [{ data: syncMeta }, { data: stats }] = await Promise.all([
        api.get("/cfdi/client-sync-meta", { params: { client_id: clientId } }),
        api.get("/sat/cfdi/stats", { params: { client_id: clientId } }).catch(() => ({ data: null })),
      ]);

      const currentYear = new Date().getFullYear();
      const years = Array.from({ length: SUMMARY_YEARS_BACK + 1 }, (_, i) => currentYear - i);
      const summaryRows = (
        await Promise.all(
          years.map((year) =>
            api
              .get("/cfdi/summary", { params: { client_id: clientId, year } })
              .then((res) => res.data)
              .catch(() => []),
          ),
        )
      ).flat();

      const { emitido, recibido } = sumCfdiSummaryByDireccion(summaryRows);
      const total =
        typeof syncMeta?.cfdi_count === "number"
          ? syncMeta.cfdi_count
          : typeof stats?.total_cfdis === "number"
            ? stats.total_cfdis
            : null;

      setStorageSnapshot({
        total,
        emitido: emitido > 0 ? emitido : null,
        recibido: recibido > 0 ? recibido : null,
        lastXml: syncMeta?.last_xml_ingested_at || stats?.data_updated_at || null,
        partialSplit: emitido + recibido > 0 && typeof total === "number" && emitido + recibido < total,
      });
    } catch {
      setStorageSnapshot(null);
    } finally {
      setStorageLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (!diagnoseEnabled || !clientId) {
      setSurgicalQueue(null);
      return undefined;
    }
    loadSurgicalQueue();
    const id = window.setInterval(loadSurgicalQueue, 30000);
    return () => window.clearInterval(id);
  }, [clientId, diagnoseEnabled, loadSurgicalQueue]);

  useEffect(() => {
    loadStorageSnapshot();
  }, [loadStorageSnapshot]);

  const saveFechaInicio = async () => {
    if (!clientId || !client) return;
    if (form.modulo_descarga_activo && !(form.fecha_inicio_descarga || "").trim()) {
      toast.error("Indique la fecha inicio de descarga histórica");
      return;
    }
    setSavingFecha(true);
    try {
      const { data } = await api.put(`/clients/${clientId}`, {
        ...client,
        fecha_inicio_descarga: (form.fecha_inicio_descarga || "").slice(0, 10) || null,
      });
      toast.success("Fecha de inicio guardada");
      onClientUpdated?.(data);
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo guardar la fecha de inicio"));
    } finally {
      setSavingFecha(false);
    }
  };

  const fechaDirty =
    client &&
    (form.fecha_inicio_descarga || "").slice(0, 10) !== (client.fecha_inicio_descarga || "").slice(0, 10);

  const ultimaRevision =
    client?.sat_sync_completed_at || client?.sat_sync_enqueued_at || client?.sat_sync_started_at || null;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="client-sat-sync-panel">
      <div className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Cliente</Label>
        <Select value={clientId || ""} onValueChange={(v) => onSelectClientId?.(v)}>
          <SelectTrigger data-testid="client-sync-client-select" className="h-auto min-h-[40px] py-2 text-left">
            <SelectValue placeholder="Seleccione un cliente del catálogo" />
          </SelectTrigger>
          <SelectContent className="max-w-[600px]">
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id} className="py-2">
                <div className="flex flex-col items-start text-left">
                  <div className="font-bold text-sm whitespace-normal leading-tight">{c.razon_social}</div>
                  <div className="text-[10px] text-slate-500 mt-1 font-mono">{c.rfc}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!clientId ? (
          <p className="text-xs text-slate-600 m-0">
            Elija el contribuyente para ver el estado SAT, los XML ya guardados en el sistema y la configuración de
            sincronización automática.
          </p>
        ) : null}
      </div>

      {clientId && client ? (
        <>
          {diagnoseEnabled && surgicalQueue ? (
            <SurgicalQueueStatusBanner surgicalQueue={surgicalQueue} />
          ) : null}

          {canShowSatAutoSync ? (
            <div className="grid grid-cols-1 gap-4">
              <ClientFechaInicioDescargaField form={form} setForm={setForm} />
              {fechaDirty ? (
                <div className="flex justify-end -mt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-sm h-8"
                    disabled={savingFecha}
                    onClick={saveFechaInicio}
                    data-testid="client-sync-save-fecha"
                  >
                    {savingFecha ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                    Guardar fecha de inicio
                  </Button>
                </div>
              ) : null}
              <ClientSatAutoSyncToggle
                form={form}
                setForm={setForm}
                clientId={clientId}
                onPersisted={(doc) => onClientUpdated?.(doc)}
              />
            </div>
          ) : null}

          <section
            className="rounded-md border border-slate-200 bg-white p-4 space-y-3"
            data-testid="client-sat-estado-section"
          >
            <h3 className="text-sm font-semibold text-[#0F2D5C] m-0 flex items-center gap-2">
              <Radar className="w-4 h-4 shrink-0" strokeWidth={1.75} />
              {CLIENT_XML_SISTEMA_LABELS.sectionEstado}
            </h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs m-0">
              <div>
                <dt className="text-[10px] uppercase tracking-wide text-slate-500">Sincronización automática</dt>
                <dd className="m-0 font-medium text-slate-800">
                  {formatSyncAutoLabel(Boolean(client.modulo_descarga_activo))}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wide text-slate-500">Estado en cola</dt>
                <dd className="m-0 font-medium text-slate-800">
                  {syncEstadoColaLabel(client.estado_sincronizacion)}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wide text-slate-500">Última revisión</dt>
                <dd className="m-0 font-medium text-slate-800">{formatClientSatTimestamp(ultimaRevision)}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wide text-slate-500">
                  Último periodo cubierto en sistema
                </dt>
                <dd className="m-0 font-medium text-slate-800">{formatPeriodoCubiertoLabel(client)}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-[10px] uppercase tracking-wide text-slate-500">Scheduler SAT (este cliente)</dt>
                <dd className="m-0 font-medium text-slate-800">{formatSchedulerClienteLabel(client)}</dd>
              </div>
            </dl>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" variant="outline" asChild data-testid="client-sync-goto-monitor">
                <Link to={`/monitor-sincronizacion-sat${clientId ? `?client=${encodeURIComponent(clientId)}` : ""}`}>
                  {CLIENT_XML_SISTEMA_LABELS.verMonitor}
                </Link>
              </Button>
            </div>
          </section>

          <section
            className="rounded-md border border-emerald-200/80 bg-emerald-50/30 p-4 space-y-3"
            data-testid="client-xml-sistema-section"
          >
            <h3 className="text-sm font-semibold text-[#0F2D5C] m-0 flex items-center gap-2">
              <FileStack className="w-4 h-4 shrink-0" strokeWidth={1.75} />
              {CLIENT_XML_SISTEMA_LABELS.sectionXml}
            </h3>
            <p className="text-[11px] text-slate-600 m-0 leading-relaxed">
              Comprobantes ya integrados en la base de datos. Exportar o empaquetar estos XML{" "}
              <strong>no consulta al SAT</strong> ni usa e.Firma.
            </p>
            {storageLoading ? (
              <p className="text-xs text-slate-500 m-0 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Consultando copia local…
              </p>
            ) : (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs m-0">
                <div>
                  <dt className="text-[10px] uppercase tracking-wide text-slate-500">Emitidos guardados</dt>
                  <dd className="m-0 font-mono font-semibold text-slate-900">
                    {storageSnapshot?.emitido != null ? storageSnapshot.emitido : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-wide text-slate-500">Recibidos guardados</dt>
                  <dd className="m-0 font-mono font-semibold text-slate-900">
                    {storageSnapshot?.recibido != null ? storageSnapshot.recibido : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-wide text-slate-500">Total en sistema</dt>
                  <dd className="m-0 font-mono font-semibold text-slate-900">
                    {storageSnapshot?.total != null ? storageSnapshot.total : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-wide text-slate-500">Último XML guardado</dt>
                  <dd className="m-0 font-medium text-slate-800">
                    {formatClientSatTimestamp(storageSnapshot?.lastXml)}
                  </dd>
                </div>
              </dl>
            )}
            {storageSnapshot?.partialSplit ? (
              <p className="text-[10px] text-slate-500 m-0">
                El desglose emitidos/recibidos refleja los últimos {SUMMARY_YEARS_BACK + 1} años; el total incluye
                todo el historial integrado.
              </p>
            ) : null}
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled
                className="rounded-sm cursor-not-allowed opacity-80"
                title={CLIENT_XML_SISTEMA_LABELS.exportarZipPendiente}
                data-testid="client-xml-zip-placeholder"
              >
                <Archive className="w-3.5 h-3.5 mr-1.5" />
                {CLIENT_XML_SISTEMA_LABELS.descargarXmlSistema}
              </Button>
              <Button size="sm" variant="outline" asChild data-testid="client-xml-goto-gestionar">
                <Link to="/gestionar-xml">
                  {CLIENT_XML_SISTEMA_LABELS.gestionarXml}
                </Link>
              </Button>
            </div>
            <p className="text-[10px] text-slate-500 m-0">{CLIENT_XML_SISTEMA_LABELS.exportarZipPendiente}</p>
          </section>
        </>
      ) : null}
    </div>
  );
}
